import type { EFileKind, EFileStatus, RecordStatus, Upload } from '@tabservice/tabfile-client';
import type { Tone } from '@/components/ui/pill';

export interface StatusMeta {
  label: string;
  tone: Tone;
  /** One line the UI can show under the status. */
  hint: string;
}

export const filingStatusMeta: Record<EFileStatus, StatusMeta> = {
  queued: { label: 'Queued', tone: 'neutral', hint: 'Waiting to be picked up for transmission.' },
  transmitting: { label: 'Transmitting', tone: 'info', hint: 'Chunks are being sent to the IRS.' },
  processing: { label: 'Processing', tone: 'info', hint: 'Awaiting IRS acknowledgement.' },
  accepted: { label: 'Accepted', tone: 'success', hint: 'All records accepted.' },
  accepted_with_errors: {
    label: 'Accepted with errors',
    tone: 'warning',
    hint: 'Accepted with record errors. Corrections required.',
  },
  partially_accepted: {
    label: 'Partly accepted',
    tone: 'warning',
    hint: 'Some records accepted, the rest rejected.',
  },
  rejected: { label: 'Rejected', tone: 'danger', hint: 'Rejected by the IRS. Send a replacement.' },
  failed: { label: 'Failed', tone: 'danger', hint: 'Transmission failed. Nothing reached the IRS.' },
};

export const recordStatusMeta: Record<RecordStatus, StatusMeta> = {
  pending: { label: 'Pending', tone: 'neutral', hint: 'Not sent yet.' },
  transmitted: { label: 'Transmitted', tone: 'info', hint: 'Sent. Awaiting acknowledgement.' },
  accepted: { label: 'Accepted', tone: 'success', hint: 'Accepted by the IRS.' },
  error: { label: 'Error', tone: 'warning', hint: 'Accepted with an error that requires a correction.' },
  rejected: { label: 'Rejected', tone: 'danger', hint: 'Refused by the IRS.' },
};

export const filingKindLabel: Record<EFileKind, string> = {
  original: 'Original',
  correction: 'Correction',
  replacement: 'Replacement',
};

type UploadStatus = NonNullable<Upload['status']>;

export const uploadStatusMeta: Record<UploadStatus, StatusMeta> = {
  awaiting_file: { label: 'Waiting for the file', tone: 'neutral', hint: 'The upload link is ready.' },
  received: { label: 'Received', tone: 'info', hint: 'File received. Validation starts next.' },
  parsing: { label: 'Reading', tone: 'info', hint: 'Reading rows and matching columns.' },
  checked: { label: 'Validated', tone: 'success', hint: 'Validation complete. Ready to submit.' },
  blocked: { label: 'Blocked', tone: 'danger', hint: 'Some rows must be fixed before submission.' },
  filed: { label: 'Filed', tone: 'success', hint: 'An e-file was created from this upload.' },
  failed: { label: 'Failed', tone: 'danger', hint: 'The file could not be read.' },
};

/** Who has to act on a record error. */
export const fixOwnerLabel: Record<string, { label: string; tone: Tone }> = {
  client: { label: 'Your action', tone: 'warning' },
  tabservice: { label: 'Tab Service action', tone: 'info' },
  unknown: { label: 'Owner unknown', tone: 'neutral' },
};

export function filingStatusOf(status: EFileStatus | undefined): StatusMeta {
  return status ? filingStatusMeta[status] : { label: 'Unknown', tone: 'neutral', hint: '' };
}
